import type { MapPoint } from '@/lib/maps/types';

import { MapOverlay } from './map-overlay';
import styles from './my-location-dot.module.scss';

export type MyLocationDotProps = {
  /** The Grower's position, or `null` — denied, undecided, or no fix (113584 §8g). */
  position: MapPoint | null;
};

/**
 * The Grower's own position: **one** solid dot, and only ever one (Figma `1322:689888`).
 *
 * Three criteria are structural here rather than enforced by a handler. It is **not interactive** —
 * `<MapOverlay>`'s container takes no pointer events and nothing here opts back in, so clicking it
 * does nothing at all (113584 §8f), unlike a Device marker, which is a `<button>`. It is
 * **geographically anchored** — the overlay re-projects it on every camera change and hides it once
 * the map's bounds no longer contain it, so panning away moves the dot out of view rather than
 * dragging the camera back (§8c). And it is **not moved by the viewport**: nothing here reads the
 * camera, so a pan or zoom cannot shift it off the coordinate it was given.
 *
 * Nothing here reads accuracy: the seam never carries it (`lib/maps/geolocation.ts`), so no accuracy
 * radius, halo or margin-of-error ring can be drawn (113583 §4c; 113584 §8b). The body's 40px is a
 * constant at every zoom and for every fix — the same 40px as a Device marker, in a different colour
 * family and with no glyph, so a position is never mistaken for a Device (113583 §4a).
 */
export function MyLocationDot({ position }: MyLocationDotProps) {
  if (!position) return null;

  return (
    <MapOverlay point={position}>
      <span className={styles.dot} role="img" aria-label="My location">
        <span className={styles.core} aria-hidden />
      </span>
    </MapOverlay>
  );
}
